import React from 'react';
import { BaseStep, FormField, useFormData, BaseStepProps } from '../BaseStep';
import FormSection from '../../../FormSection';
import supportIcon from '../../../../assets/icons/support.png';
import hearIcon from '../../../../assets/icons/hear.png';
import expertiseIcon from '../../../../assets/icons/expertise.png';
import trophyIcon from '../../../../assets/trophy.png';
import { Toggle } from '../Toggle';
import { useProfile } from '../../../../contexts/ProfileContext';
import {
  expertiseSelectOptions,
  updateFrequencySelectOptions,
} from '../../../../constants/support';

const ExternalSupportStep: React.FC<BaseStepProps> = ({
  userType,
  onCreateProfile,
  hasProfile,
  onComplete,
  shouldValidate,
  onValidationHandled,
  onValidationResult,
}) => {
  const { startupProfile } = useProfile();

  // Helper functions for backward compatibility
  const stringToArray = (value: string | string[] | undefined): string[] => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string' && value) {
      return value
        .split(',')
        .map(s => s.trim())
        .filter(s => s);
    }
    return [];
  };

  const stringToBoolean = (value: string | boolean | undefined): boolean | undefined => {
    if (typeof value === 'boolean') return value;
    if (value === 'true') return true;
    if (value === 'false') return false;
    return undefined;
  };

  // Initialize values from profile
  const hasParticipatedValue = stringToBoolean(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (startupProfile as any)?.hasParticipatedInProgram
  );
  const programNamesArray = stringToArray(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (startupProfile as any)?.programNames
  );
  const openToHearingValue = stringToBoolean(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (startupProfile as any)?.openToHearing
  );

  const { formData, errors, handleInputChange, validateForm } = useFormData({
    investorExpertise:
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (startupProfile as any)?.investorExpertise || '',
    otherExpertise:
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (startupProfile as any)?.otherExpertise || '',
    updateFrequency:
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (startupProfile as any)?.updateFrequency || '',
  });

  // Local state for boolean toggles
  const [hasParticipated, setHasParticipated] = React.useState<boolean>(
    hasParticipatedValue ?? false
  );
  const [isOpenToHearing, setIsOpenToHearing] = React.useState<boolean>(
    openToHearingValue ?? false
  );
  // Raw text so a space in "Y Combinator" is not trimmed away while typing.
  const [programNamesText, setProgramNamesText] = React.useState(programNamesArray.join(', '));
  const hearingTouchedRef = React.useRef(false);

  // Profile can arrive after the first render. Don't clobber a toggle the user already moved.
  React.useEffect(() => {
    if (hearingTouchedRef.current) return;
    if (typeof openToHearingValue === 'boolean') {
      setIsOpenToHearing(openToHearingValue);
    }
  }, [openToHearingValue]);

  const programNamesParsed = programNamesText
    .split(',')
    .map(name => name.trim())
    .filter(Boolean);
  const supportPayload = {
    ...formData,
    hasParticipatedInProgram: hasParticipated,
    // Clear names when Support is off so the profile does not keep the previous option.
    programNames: hasParticipated ? programNamesParsed : [],
    openToHearing: isOpenToHearing,
    otherExpertise:
      formData.investorExpertise === 'looking-for-other-support' ? formData.otherExpertise : '',
  };
  const supportPayloadRef = React.useRef(supportPayload);
  supportPayloadRef.current = supportPayload;

  // Publish before paint so Save reads the contacts toggle, not a stale empty value.
  React.useLayoutEffect(() => {
    onCreateProfile?.(supportPayloadRef.current);
  }, [formData, programNamesText, hasParticipated, isOpenToHearing, onCreateProfile]);

  // State to control when to show validation errors
  const [showErrors, setShowErrors] = React.useState(false);

  // Listen for validation requests from parent. Pass the live payload so the contacts toggle is saved.
  React.useLayoutEffect(() => {
    if (!shouldValidate) return;
    const requiredFields: string[] = [];
    const isValid = validateForm(requiredFields);

    setShowErrors(true);

    if (onValidationResult) {
      onValidationResult(isValid, supportPayloadRef.current);
    }

    if (onValidationHandled) {
      onValidationHandled();
    }

    if (isValid) {
      setTimeout(() => {
        setShowErrors(false);
      }, 100);
    }
  }, [shouldValidate, validateForm, onValidationHandled, onValidationResult]);

  return (
    <BaseStep
      userType={userType}
      onCreateProfile={onCreateProfile}
      hasProfile={hasProfile}
      onComplete={onComplete}
    >
      <FormSection icon={supportIcon} title="SUPPORT">
        <div className="flex items-center justify-between">
          <label className="text-sm sm:text-base font-medium text-white text-left">
            The company has participated in a generator, incubator or accelerator program
          </label>
          <Toggle
            name="hasParticipatedInProgram"
            label=""
            value={hasParticipated}
            onChange={setHasParticipated}
          />
        </div>

        {hasParticipated && (
          <div>
            <label
              htmlFor="programNames"
              className="block text-sm sm:text-base font-medium text-white mb-2 text-left"
            >
              Which one(s)?
            </label>
            <input
              type="text"
              id="programNames"
              name="programNames"
              value={programNamesText}
              onChange={e => setProgramNamesText(e.target.value)}
              placeholder="Program names separated with comma ','"
              className={`w-full px-2 py-1.5 sm:px-3 sm:py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-gray-900 ${
                showErrors && errors.programNames ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {showErrors && errors.programNames && (
              <p className="text-red-500 text-sm mt-1 text-right">{errors.programNames}</p>
            )}
          </div>
        )}
      </FormSection>

      <FormSection icon={hearIcon} title="CONTACTS">
        <div className="flex items-center justify-between">
          <label className="text-sm sm:text-base font-medium text-white text-left">
            Would you be open to hearing from people who help startups grow? (Accelerators,
            consultants, etc.)
          </label>
          <Toggle
            name="openToHearing"
            label=""
            value={isOpenToHearing}
            onChange={value => {
              hearingTouchedRef.current = true;
              setIsOpenToHearing(value);
            }}
          />
        </div>
      </FormSection>

      <FormSection icon={expertiseIcon} title="EXPERTISE">
        <div>
          <label
            htmlFor="investorExpertise"
            className="block text-sm sm:text-base font-medium text-white mb-2 text-left"
          >
            Most important expertise you look for from an Investor (how &apos;smart&apos; is their
            money?).
          </label>
          <select
            id="investorExpertise"
            name="investorExpertise"
            value={String(formData.investorExpertise || '')}
            onChange={handleInputChange}
            className={`w-full px-2 py-1.5 sm:px-3 sm:py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-gray-900 ${
              showErrors && errors.investorExpertise ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            {expertiseSelectOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.name}
              </option>
            ))}
          </select>
          {showErrors && errors.investorExpertise && (
            <p className="text-red-500 text-sm mt-1 text-right">{errors.investorExpertise}</p>
          )}
        </div>

        {formData.investorExpertise === 'looking-for-other-support' && (
          <FormField
            name="otherExpertise"
            label="What expertise?"
            value={String(formData.otherExpertise || '')}
            onChange={handleInputChange}
            placeholder="Specify the expertise"
            error={showErrors ? errors.otherExpertise : undefined}
          />
        )}
      </FormSection>

      <FormSection icon={trophyIcon} title="PROGRESS">
        <div>
          <label
            htmlFor="updateFrequency"
            className="block text-sm sm:text-base font-medium text-white mb-2 text-left"
          >
            Remind me to review/update my data profile:
          </label>
          <select
            id="updateFrequency"
            name="updateFrequency"
            value={String(formData.updateFrequency || '')}
            onChange={handleInputChange}
            className={`w-full px-2 py-1.5 sm:px-3 sm:py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-gray-900 ${
              showErrors && errors.updateFrequency ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            {updateFrequencySelectOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.name}
              </option>
            ))}
          </select>
          {showErrors && errors.updateFrequency && (
            <p className="text-red-500 text-sm mt-1 text-right">{errors.updateFrequency}</p>
          )}
        </div>
      </FormSection>
    </BaseStep>
  );
};

export default ExternalSupportStep;
